import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import FollowupFormModal from '../components/FollowupFormModal';
import ConvertModal from '../components/ConvertModal';
import './EnquiryDetail.css';
import { toast } from '../components/Toast';

function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertType, setConvertType] = useState('');

  useEffect(() => {
    fetchEnquiryDetails();
    fetchFollowups();
  }, [id]);

  const fetchEnquiryDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/enquiries/${id}`);
      const enquiryData = response.data.data || response.data;
      setEnquiry(enquiryData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch enquiry details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowups = async () => {
    try {
      const response = await axios.get(`/enquiries/${id}/followups`);
      const followupData = response.data.data || response.data;
      setFollowups(followupData);
    } catch (err) {
      console.error('Failed to fetch followups:', err);
    }
  };

  const handleAddFollowup = () => {
    setShowFollowupModal(true);
  };

  const handleFollowupSuccess = () => {
    setShowFollowupModal(false);
    fetchFollowups();
    showSuccessMessage('Follow-up added successfully');
  };

  const handleConvertToCustomer = () => {
    setConvertType('customer');
    setShowConvertModal(true);
  };

  const handleConvertToProject = () => {
    setConvertType('project');
    setShowConvertModal(true);
  };

  const handleConvertSuccess = () => {
    setShowConvertModal(false);
    showSuccessMessage(`Successfully converted to ${convertType}`);
    fetchEnquiryDetails();
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      NEW: 'badge-new',
      FOLLOWUP: 'badge-followup',
      CUSTOMER_CONVERTED: 'badge-converted',
      PROJECT_CONVERTED: 'badge-converted',
      LOST: 'badge-lost'
    };
    return statusClasses[status] || '';
  };

  if (loading) {
    return <div className="loading">Loading enquiry details...</div>;
  }

  if (!enquiry) {
    return (
      <div className="enquiry-detail-container">
        <p style={{ margin: '20px 0' }}>Enquiry not found.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/enquiries')}>
          Back to Enquiries
        </button>
      </div>
    );
  }

  return (
    <div className="enquiry-detail-container">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/enquiries')}>
            ← Back to Enquiries
          </button>
          <h1>Enquiry Details</h1>
        </div>
        <div className="header-actions">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <button
              className="btn btn-success"
              onClick={handleConvertToCustomer}
              disabled={enquiry.convertedToCustomer}
            >
              Convert to Customer
            </button>
            {enquiry.status === 'CUSTOMER_CONVERTED' && (
              <span className="converted-indicator">✓ Customer Converted</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <button
              className="btn btn-primary"
              onClick={handleConvertToProject}
              disabled={enquiry.convertedToProject}
            >
              Convert to Project
            </button>
            {enquiry.status === 'PROJECT_CONVERTED' && (
              <span className="converted-indicator">✓ Project Converted</span>
            )}
          </span>
        </div>
      </div>



      <div className="enquiry-info-card">
        <div className="card-header">
          <h2>Enquiry Information</h2>
          <span className={`badge ${getStatusBadgeClass(enquiry.status)}`}>
            {enquiry.status}
          </span>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <label>Enquiry ID</label>
            <span>{enquiry.id}</span>
          </div>
          <div className="info-item">
            <label>Customer Name</label>
            <span>{enquiry.customerName}</span>
          </div>
          <div className="info-item">
            <label>Phone</label>
            <span>{enquiry.phone}</span>
          </div>
          <div className="info-item">
            <label>Email</label>
            <span>{enquiry.email || '-'}</span>
          </div>
          <div className="info-item">
            <label>Source</label>
            <span>{enquiry.source || '-'}</span>
          </div>
          <div className="info-item">
            <label>Alternate Phone</label>
            <span>{enquiry.alternatePhone || '-'}</span>
          </div>
          <div className="info-item">
            <label>Care Of (C/O)</label>
            <span>{enquiry.careOf || '-'}</span>
          </div>
          <div className="info-item">
            <label>Second Contact Name</label>
            <span>{enquiry.secondContactName || '-'}</span>
          </div>
          <div className="info-item">
            <label>Second Contact Number</label>
            <span>{enquiry.secondContact || '-'}</span>
          </div>
          <div className="info-item">
            <label>Assigned To</label>
            <span>{enquiry.assignedTo ? enquiry.assignedTo.name : 'Unassigned'}</span>
          </div>
          <div className="info-item">
            <label>Created At</label>
            <span>{new Date(enquiry.createdAt).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <label>Last Updated</label>
            <span>{new Date(enquiry.updatedAt).toLocaleString()}</span>
          </div>
          <div className="info-item full-width">
            <label>Project Requirement</label>
            <p className="requirement-text">{enquiry.projectRequirement}</p>
          </div>
          {enquiry.notes && (
            <div className="info-item full-width">
              <label>Notes</label>
              <p className="requirement-text">{enquiry.notes}</p>
            </div>
          )}
        </div>
      </div>

      <div className="followup-section">
        <div className="section-header">
          <h2>Follow-up Timeline</h2>
          <button className="btn btn-primary" onClick={handleAddFollowup}>
            Add Follow-up
          </button>
        </div>

        {followups.length === 0 ? (
          <div className="no-followups">
            <p>No follow-ups recorded yet.</p>
            <button className="btn btn-primary" onClick={handleAddFollowup}>
              Add First Follow-up
            </button>
          </div>
        ) : (
          <div className="timeline">
            {followups.map((followup) => (
              <div key={followup.id} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-user">
                      {followup.createdBy ? followup.createdBy.name : 'Unknown'}
                    </span>
                    <span className="timeline-date">
                      {new Date(followup.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="timeline-notes">{followup.notes}</p>
                  {(followup.nextFollowupDate || followup.nextFollowupTime) && (
                    <div className="next-followup">
                      <strong>Next Follow-up:</strong> {followup.nextFollowupDate ? new Date(followup.nextFollowupDate).toLocaleDateString() : ''}
                      {followup.nextFollowupTime ? ` at ${followup.nextFollowupTime.substring(0, 5)}` : ''}
                      {followup.assignedTo && (
                        <span style={{ marginLeft: '8px', color: '#6366f1', fontWeight: '500' }}>
                          (Assigned to: {followup.assignedTo.name})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFollowupModal && (
        <FollowupFormModal
          enquiryId={id}
          defaultAssignedToId={enquiry?.assignedTo?.id}
          onClose={() => setShowFollowupModal(false)}
          onSuccess={handleFollowupSuccess}
        />
      )}

      {showConvertModal && (
        <ConvertModal
          enquiryId={id}
          enquiry={enquiry}
          convertType={convertType}
          onClose={() => setShowConvertModal(false)}
          onSuccess={handleConvertSuccess}
        />
      )}
    </div>
  );
}

export default EnquiryDetail;
